import { LegalPage } from './LegalPage';
import { LEGAL_CONFIG, LEGAL_PATHS } from './config';

const { entity, product, domain, privacyEmail, supportEmail } = LEGAL_CONFIG;

/**
 * Privacy Policy.
 *
 * Written against what the service actually does: Firebase authentication,
 * asset storage in Google Cloud Storage, AI processing through third-party
 * model providers, OAuth connections to YouTube/TikTok/Meta for publishing
 * and analytics, and Stripe-hosted billing.
 *
 * The "Google user data" section exists because the app requests sensitive
 * YouTube scopes; OAuth verification checks for these specific disclosures,
 * including the Limited Use statement. Do not remove it while those scopes
 * are requested.
 */
export function PrivacyPolicy() {
    return (
        <LegalPage title="Privacy Policy">
            <p>
                This policy explains what {entity} (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects when you
                use {product} at {domain} (the &ldquo;Service&rdquo;), why we collect it, who we share it
                with, and what control you have over it. It applies to the {product} web application
                and the APIs behind it.
            </p>

            <h2>1. Information we collect</h2>

            <h3>Account information</h3>
            <p>
                You sign in through Firebase Authentication. We receive and store your email address,
                a Firebase user identifier, the username you choose, your account role and status,
                account creation and update timestamps, and a record that you accepted these terms
                along with the version you accepted. We do not receive or store your password.
            </p>

            <h3>Content you upload and create</h3>
            <p>
                We store the video, image, and audio files you upload, the projects and workspaces you
                organize them into, the edits and scripts you produce, the outputs the Service
                generates, and any comments, prompts, titles, descriptions, and other text you enter.
                Files are held in Google Cloud Storage; project metadata is held in Google Cloud
                Firestore. Both are operated by us under our own cloud accounts.
            </p>

            <h3>Connected social accounts</h3>
            <p>
                If you connect a YouTube, TikTok, Facebook, or Instagram account, we store the OAuth
                access and refresh tokens that platform issues, along with the channel or account
                identifier and display name. We use them only to perform actions you ask for &mdash;
                publishing content you approve and retrieving performance metrics for content
                published through the Service.
            </p>

            <h3>Billing information</h3>
            <p>
                Payments are processed by Stripe through Stripe-hosted checkout.{' '}
                <strong>Card numbers and payment credentials never reach our servers.</strong> We
                store the Stripe customer and payment identifiers Stripe returns, whether a payment
                method is on file, your prepaid account balance, and a record of each top-up and
                each operation charged against it (what ran, when, and what it cost).
            </p>

            <h3>Technical and usage information</h3>
            <p>
                Our servers and our cloud providers record standard operational data: IP address,
                browser and device type, request timestamps and paths, and error diagnostics. We use
                this to operate the Service, investigate faults, and detect abuse.
            </p>

            <h2>2. How we use information</h2>
            <ul>
                <li>To authenticate you and run the Service.</li>
                <li>To store, process, edit, analyze, and render the content you give us.</li>
                <li>To publish content to the platforms you have connected, when you ask us to.</li>
                <li>To report performance metrics for content published through the Service.</li>
                <li>To measure what each operation costs, draw it from your balance, and process top-ups through Stripe.</li>
                <li>To provide support, and to send service and billing notices.</li>
                <li>To maintain security, prevent abuse, and comply with legal obligations.</li>
            </ul>
            <div className="legal-callout">
                <p>
                    <strong>We do not sell your personal information, and we do not use your content
                    to train our own machine-learning models.</strong> We do not serve advertising and
                    we do not share your content with advertisers or data brokers.
                </p>
            </div>

            <h2>3. AI processing</h2>
            <p>
                {product} uses third-party AI model providers &mdash; currently Anthropic, OpenAI, and
                Google &mdash; to analyze video, plan and generate edits, produce suggestions, and
                generate media. To do this we transmit relevant parts of your content and prompts to
                those providers through their commercial APIs.
            </p>
            <div className="legal-callout">
                <p>
                    We use these providers under commercial API terms that prohibit them from using
                    submitted content to train their models. We do not control their infrastructure,
                    and we cannot guarantee an outcome produced by an AI system is accurate, original,
                    or suitable for your purpose &mdash; review output before you publish it.
                </p>
            </div>
            <p>
                The specific providers may change as the Service evolves. Where a change would
                materially alter how your content is handled, we will update this policy and the
                effective date above.
            </p>

            <h2>4. Google user data and YouTube</h2>
            <p>
                When you connect a YouTube account, you grant permissions that let the Service upload
                and manage videos on your channel, and read your channel&apos;s analytics, including
                revenue metrics where you have granted that scope. We use this access solely to
                perform the publishing and reporting actions you request inside {product}.
            </p>
            <div className="legal-callout">
                <p>
                    {product}&apos;s use and transfer of information received from Google APIs to any
                    other app will adhere to the{' '}
                    <a
                        href="https://developers.google.com/terms/api-services-user-data-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Google API Services User Data Policy
                    </a>
                    , including the Limited Use requirements.
                </p>
            </div>
            <p>
                We do not transfer Google user data to third parties except as necessary to provide
                the Service, comply with applicable law, or as part of a merger or acquisition. We do
                not use Google user data for advertising, and we do not allow humans to read it except
                with your explicit consent, to resolve a support issue you have raised, for security
                purposes, or where required by law.
            </p>
            <p>
                By using the YouTube features of the Service you also agree to the{' '}
                <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
                    YouTube Terms of Service
                </a>
                . Google&apos;s handling of your data is described in the{' '}
                <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Google Privacy Policy
                </a>
                . You can revoke the Service&apos;s access to your Google account at any time through{' '}
                <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Google security settings
                </a>
                , or by disconnecting the channel inside {product}.
            </p>

            <h2>5. Who we share information with</h2>
            <p>
                We share information with service providers who process it on our behalf, under
                contract, only to deliver the Service:
            </p>
            <table className="legal-table">
                <thead>
                    <tr>
                        <th>Provider</th>
                        <th>Purpose</th>
                        <th>What it receives</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Google Cloud &amp; Firebase</td>
                        <td>Hosting, authentication, file and database storage</td>
                        <td>Account data, uploaded and generated content, technical logs</td>
                    </tr>
                    <tr>
                        <td>Anthropic, OpenAI, Google</td>
                        <td>AI analysis, planning, and generation</td>
                        <td>Content and prompts submitted for processing</td>
                    </tr>
                    <tr>
                        <td>Stripe</td>
                        <td>Payment processing and billing</td>
                        <td>Email, billing details, and top-up amounts</td>
                    </tr>
                    <tr>
                        <td>YouTube, TikTok, Meta</td>
                        <td>Publishing and analytics on accounts you connect</td>
                        <td>Content you choose to publish, and your OAuth authorization</td>
                    </tr>
                </tbody>
            </table>
            <p>
                We may also disclose information where required by law or valid legal process, to
                enforce our terms, to protect the rights and safety of users or the public, or in
                connection with a merger, acquisition, or sale of assets &mdash; in which case we will
                give notice before your information becomes subject to a different policy.
            </p>

            <h2>6. Retention and deletion</h2>
            <p>
                We keep your account data for as long as your account is active. You can delete
                individual files, projects, and workspaces at any time from within the Service, and
                disconnect any social account, which removes the stored tokens for it.
            </p>
            <p>
                Project content is not kept indefinitely. Projects and workspaces that have gone
                unused for an extended period, and the media stored in them, may be deleted; files
                kept only as an intermediate step toward a finished result may be removed once that
                result is stored. We email you in advance before deleting anything for inactivity,
                and using the project again cancels the deletion. The{' '}
                <a href={LEGAL_PATHS.terms}>Terms of Service</a> set out the retention periods and
                the notice you get.
            </p>
            <p>
                To delete your account entirely, contact us at{' '}
                <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>. We will delete your account,
                stored content, and connected-account credentials within 30 days, except where we must
                retain records to meet legal, tax, or accounting obligations &mdash; billing records
                in particular are retained as required by law. Backups are purged on a rolling
                schedule. Content you already published to a third-party platform stays there; you
                must delete it on that platform.
            </p>

            <h2>7. Security</h2>
            <p>
                We use encryption in transit, encryption at rest through our cloud provider, scoped
                service credentials, secret management for keys and tokens, and access controls that
                limit administrative access to personnel who need it. No system is perfectly secure,
                and we cannot guarantee absolute security, but we will notify affected users and any
                required authority of a breach involving personal data as the law requires.
            </p>

            <h2>8. Your rights</h2>
            <p>
                Depending on where you live, you may have the right to access, correct, export, or
                delete your personal information, to object to or restrict certain processing, and to
                withdraw consent. Residents of California, the EEA, and the UK have specific rights
                under the CCPA/CPRA, GDPR, and UK GDPR respectively, including the right not to be
                discriminated against for exercising them.
            </p>
            <p>
                To exercise any of these, email{' '}
                <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>. We will verify your identity
                against your account before acting, and we respond within the time the applicable law
                allows. EEA and UK users may also complain to their local supervisory authority.
            </p>

            <h2>9. International transfers</h2>
            <p>
                We operate in the United States, and our providers may process data in the United
                States and other countries. Where we transfer personal data out of the EEA or UK, we
                rely on the European Commission&apos;s Standard Contractual Clauses or another
                approved transfer mechanism.
            </p>

            <h2>10. Children</h2>
            <p>
                The Service is not directed to children. You must be at least 18 years old, or the age
                of majority where you live, to hold an account. We do not knowingly collect personal
                information from children under 13, and we delete it if we learn we have.
            </p>

            <h2>11. Cookies and local storage</h2>
            <p>
                We use cookies and browser local storage for authentication, session continuity, and
                remembering interface preferences &mdash; specifically, the Firebase Authentication
                session that keeps you signed in across reloads, and a small number of keys holding
                interface state.
            </p>
            <div className="legal-callout">
                <p>
                    <strong>All of it is strictly necessary to run the Service.</strong> We do not use
                    advertising, cross-site tracking, or analytics cookies, and we do not set anything
                    that would require your consent under GDPR or the ePrivacy Directive. Blocking
                    this storage will prevent you from signing in.
                </p>
            </div>
            <p>
                We show a notice about this on your first visit, and you can bring it back at any
                time using the <strong>Cookie preferences</strong> link in the footer. If we later
                introduce optional analytics cookies, that notice becomes a genuine choice: nothing
                optional will be set unless you accept it, and you will be able to withdraw acceptance
                from the same link.
            </p>

            <h2>12. Changes to this policy</h2>
            <p>
                We may update this policy. When we do, we revise the effective date above, and for
                material changes we give notice in the Service or by email before the change takes
                effect. Continued use after a change takes effect means you accept the updated policy.
            </p>

            <h2>13. Contact</h2>
            <p>
                Privacy questions and requests: <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>
                <br />
                General support: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                {LEGAL_CONFIG.mailingAddress ? (
                    <>
                        <br />
                        {entity}, {LEGAL_CONFIG.mailingAddress}
                    </>
                ) : null}
            </p>
            <p>
                See also our <a href={LEGAL_PATHS.terms}>Terms of Service</a>.
            </p>
        </LegalPage>
    );
}

export default PrivacyPolicy;
